import { memo, useContext } from 'react'
import { Handle, Position, type NodeProps, useReactFlow } from 'reactflow'
import { ExecutionContext } from '../../../components/flow-nodes/shared/ExecutionContext'
import type { BinderNodeData } from '../../../types/flow'

// Handle 样式常量
const HANDLE_STYLE = {
  width: 12,
  height: 12,
  background: '#94a3b8',
  border: '2px solid #64748b',
} as const

const HANDLE_STYLE_ACTIVE = {
  ...HANDLE_STYLE,
  background: '#06b6d4',
  border: '2px solid #0891b2',
} as const

// 自定义节点组件：Binder 节点（并行分支编排器）
export const BinderNode = memo(({ data, selected, id }: NodeProps<BinderNodeData>) => {
  const { activeNodeId, visitedNodeIds } = useContext(ExecutionContext)
  const { getEdges } = useReactFlow()
  const isActive = activeNodeId === id
  const isVisited = visitedNodeIds.has(id)
  
  // 获取从此节点出去的边数量
  const outgoingEdges = getEdges().filter(edge => edge.source === id)
  const branchCount = outgoingEdges.length
  const primaryBranches = outgoingEdges.filter(edge => edge.data?.is_primary).length
  const silentBranches = outgoingEdges.filter(edge => edge.data?.silent).length
  
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-white border-2 min-w-[180px] transition-all duration-300 ${
      isActive 
        ? 'border-cyan-500 ring-4 ring-cyan-400/50 shadow-xl scale-105 animate-pulse' 
        : selected 
          ? 'border-cyan-500 ring-2 ring-cyan-500/20 shadow-xl' 
          : isVisited 
            ? 'border-cyan-300 ring-2 ring-cyan-200/50' 
            : 'border-slate-200'
    }`}>
      <Handle
        type="target"
        position={Position.Left}
        style={selected || isActive ? HANDLE_STYLE_ACTIVE : HANDLE_STYLE}
      />
      
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md transition-colors duration-300 ${isActive ? 'bg-cyan-500' : 'bg-cyan-100'}`}>
          <span className={`text-sm ${isActive ? 'text-white' : 'text-cyan-600'}`}>🔗</span>
        </div>
        <div className="flex-1">
          <div className="font-medium text-sm">{data.label}</div>
          <div className="text-xs text-slate-500">
            {branchCount === 0 
              ? '未连接分支' 
              : `${branchCount} 个分支`
            }
          </div>
        </div>
      </div>

      {/* 分支统计信息 */}
      {branchCount > 0 && (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
          {primaryBranches > 0 && (
            <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              {primaryBranches} 主分支
            </span>
          )}
          {silentBranches > 0 && (
            <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">
              {silentBranches} 静默
            </span>
          )}
          {data.timeout && data.timeout !== 30 && (
            <span className="text-slate-400">
              {data.timeout}s超时
            </span>
          )}
        </div>
      )}
      
      <Handle
        type="source"
        position={Position.Right}
        style={selected || isActive ? HANDLE_STYLE_ACTIVE : HANDLE_STYLE}
      />
    </div>
  )
})

BinderNode.displayName = 'BinderNode'